import React, {
  createContext, useEffect, useReducer, useRef
} from 'react';
import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';
import SplashScreen from 'src/components/SplashScreen';
import {
  gql, useApolloClient, useMutation, useQuery
} from '@apollo/client';
import {
  SEND_ADMIN_OTP,
  SETUP_ADMIN_OTP,
  USE_ADMIN_OTP_BYPASS,
  VERIFY_ADMIN_BACKUP_CODE,
  VERIFY_ADMIN_OTP
} from 'src/gql/admin';
import { getOtpRoute, OTP_PHASE } from 'src/utils/adminOtp';

const CHALLENGE_STORAGE_KEY = 'adminOtpChallenge';
const ENROLLMENT_CODES_STORAGE_KEY = 'adminOtpEnrollmentCodes';

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  otpPending: false,
  otpEnabled: false,
  otpPhase: null,
  maskedMobile: null,
  enrollmentCodes: null,
  otpSendFailed: false,
  otpSendError: null,
  user: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE':
      return { ...state, isInitialised: true, ...action.payload };
    case 'LOGIN':
      return {
        ...initialAuthState,
        isAuthenticated: true,
        isInitialised: true,
        user: action.payload.user
      };
    case 'OTP_PENDING':
      return {
        ...state,
        isAuthenticated: false,
        isInitialised: true,
        otpPending: true,
        otpEnabled: action.payload.otpEnabled,
        otpPhase: action.payload.otpPhase,
        maskedMobile: action.payload.maskedMobile || null,
        enrollmentCodes: action.payload.enrollmentCodes || null,
        user: action.payload.user || state.user
      };
    case 'OTP_SETUP_DONE':
      return {
        ...state,
        otpPhase: OTP_PHASE.ENROLLMENT,
        maskedMobile: action.payload.maskedMobile,
        enrollmentCodes: action.payload.enrollmentCodes
      };
    case 'OTP_PHASE':
      return { ...state, otpPhase: action.payload.phase };
    case 'OTP_SEND_FAILED':
      return {
        ...state,
        otpPhase: OTP_PHASE.SEND_FAILED,
        otpSendFailed: true,
        otpSendError: action.payload.error
      };
    case 'CLEAR_OTP_SEND_FAILED':
      return { ...state, otpSendFailed: false, otpSendError: null };
    case 'LOGOUT':
      return { ...initialAuthState, isInitialised: true };
    default:
      return state;
  }
};

const LOGIN = gql`
  mutation LoginAdmin($input: UserLogIn) {
    loginAdmin(input: $input) {
      accessToken challengeToken challengeExpiresAt otpRequired otpEnabled maskedMobile
      userAccount { _id fname lname email role status otp_enabled otp_login_disabled created_at updated_at }
    }
  }
`;
const ME = gql`query Me { me { _id fname lname email role status otp_enabled otp_login_disabled created_at updated_at } }`;

const clearPendingStorage = () => {
  localStorage.removeItem(CHALLENGE_STORAGE_KEY);
  sessionStorage.removeItem(ENROLLMENT_CODES_STORAGE_KEY);
};

const clearAbilityStorage = () => {
  localStorage.removeItem('sp_rolesAndActions');
  localStorage.removeItem('sp_userActions');
};

const getStoredChallenge = () => {
  try {
    const value = JSON.parse(localStorage.getItem(CHALLENGE_STORAGE_KEY));
    if (!value || value.expiresAt <= Date.now() / 1000) {
      clearPendingStorage();
      return null;
    }
    return value;
  } catch (_) {
    clearPendingStorage();
    return null;
  }
};

const getStoredEnrollmentCodes = (challenge) => {
  try {
    const value = JSON.parse(sessionStorage.getItem(ENROLLMENT_CODES_STORAGE_KEY));
    if (
      !value
      || !challenge
      || value.challengeToken !== challenge.token
      || value.expiresAt !== challenge.expiresAt
    ) {
      sessionStorage.removeItem(ENROLLMENT_CODES_STORAGE_KEY);
      return null;
    }
    return value.codes;
  } catch (_) {
    sessionStorage.removeItem(ENROLLMENT_CODES_STORAGE_KEY);
    return null;
  }
};

const setSession = (accessToken) => {
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  else localStorage.removeItem('accessToken');
};

const getAuthenticatedRoute = (userAccount) => (
  userAccount && userAccount.role === 'EXAM_CENTER'
    ? '/app/exam-center'
    : '/app/dashboard'
);

const isValidToken = (accessToken) => {
  if (!accessToken) return false;
  try { return jwtDecode(accessToken).exp > Date.now() / 1000; } catch (_) { return false; }
};

const AuthContext = createContext({
  ...initialAuthState,
  login: () => Promise.resolve(),
  logout: () => {},
  sendOtp: () => Promise.resolve(),
  setupOtp: () => Promise.resolve(),
  verifyOtp: () => Promise.resolve(),
  verifyBackupCode: () => Promise.resolve(),
  useBypassToken: () => Promise.resolve(),
  setOtpPhase: () => {},
  clearOtpSendFailed: () => {}
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const apolloClient = useApolloClient();
  const cacheResetRef = useRef(Promise.resolve());
  const accessToken = localStorage.getItem('accessToken');
  const [loginAdmin] = useMutation(LOGIN);
  const [sendAdminOtp] = useMutation(SEND_ADMIN_OTP);
  const [setupAdminOtp] = useMutation(SETUP_ADMIN_OTP);
  const [verifyAdminOtp] = useMutation(VERIFY_ADMIN_OTP);
  const [verifyAdminBackupCode] = useMutation(VERIFY_ADMIN_BACKUP_CODE);
  const [useAdminOtpBypass] = useMutation(USE_ADMIN_OTP_BYPASS);
  const { loading, error, data } = useQuery(ME, {
    skip: !isValidToken(accessToken),
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const challengeToken = () => {
    const challenge = getStoredChallenge();
    if (!challenge) throw new Error('Login challenge expired. Sign in again.');
    return challenge.token;
  };

  const storeChallenge = (challenge) => {
    localStorage.setItem(CHALLENGE_STORAGE_KEY, JSON.stringify(challenge));
    return challenge;
  };

  const updateChallenge = (updates) => {
    const current = getStoredChallenge();
    if (!current) throw new Error('Login challenge expired. Sign in again.');
    return storeChallenge({ ...current, ...updates });
  };

  const saveChallenge = (payload) => storeChallenge({
    token: payload.challengeToken,
    expiresAt: payload.challengeExpiresAt,
    otpEnabled: payload.otpEnabled,
    maskedMobile: payload.maskedMobile,
    phase: payload.otpEnabled ? OTP_PHASE.VERIFICATION : OTP_PHASE.ENROLLMENT,
    resumePhase: null,
    enrollmentReady: false
  });

  const completeAuthentication = async (result) => {
    await cacheResetRef.current;
    await apolloClient.clearStore();
    clearAbilityStorage();
    setSession(result.accessToken);
    clearPendingStorage();
    dispatch({ type: 'LOGIN', payload: { user: result.userAccount } });
    return { ...result, redirectTo: getAuthenticatedRoute(result.userAccount) };
  };

  const setOtpPhase = (phase) => {
    const current = getStoredChallenge();
    const resumePhase = current && current.phase === OTP_PHASE.SEND_FAILED
      ? current.resumePhase
      : current && current.resumePhase;
    updateChallenge({ phase, resumePhase });
    dispatch({ type: 'OTP_PHASE', payload: { phase } });
  };

  const markSendFailure = (err, resumePhase) => {
    updateChallenge({ phase: OTP_PHASE.SEND_FAILED, resumePhase });
    dispatch({ type: 'OTP_SEND_FAILED', payload: { error: err.message } });
  };

  const login = async (email, password) => {
    await cacheResetRef.current;
    await apolloClient.clearStore();
    clearAbilityStorage();
    const response = await loginAdmin({ variables: { input: { mobile: email, password } } });
    const payload = response.data.loginAdmin;
    if (!payload.otpRequired) {
      return completeAuthentication(payload);
    }

    setSession(null);
    sessionStorage.removeItem(ENROLLMENT_CODES_STORAGE_KEY);
    const challenge = saveChallenge(payload);
    dispatch({
      type: 'OTP_PENDING',
      payload: {
        otpEnabled: challenge.otpEnabled,
        otpPhase: challenge.phase,
        maskedMobile: challenge.maskedMobile,
        user: payload.userAccount
      }
    });

    if (challenge.otpEnabled) {
      try {
        await sendAdminOtp({ variables: { challengeToken: challenge.token } });
      } catch (err) {
        markSendFailure(err, OTP_PHASE.VERIFICATION);
        return { redirectTo: getOtpRoute(OTP_PHASE.SEND_FAILED) };
      }
    }
    return { redirectTo: getOtpRoute(challenge.phase) };
  };

  const setupOtp = async (mobile) => {
    const response = await setupAdminOtp({
      variables: { challengeToken: challengeToken(), mobile }
    });
    const result = response.data.setupAdminOtp;
    const challenge = updateChallenge({
      phase: OTP_PHASE.ENROLLMENT,
      resumePhase: null,
      enrollmentReady: true,
      maskedMobile: result.maskedMobile
    });
    sessionStorage.setItem(ENROLLMENT_CODES_STORAGE_KEY, JSON.stringify({
      challengeToken: challenge.token,
      expiresAt: challenge.expiresAt,
      codes: result.backupCodes
    }));
    dispatch({
      type: 'OTP_SETUP_DONE',
      payload: { maskedMobile: result.maskedMobile, enrollmentCodes: result.backupCodes }
    });
    return result;
  };

  const sendOtp = async () => {
    const challenge = getStoredChallenge();
    if (!challenge) throw new Error('Login challenge expired. Sign in again.');
    const resumePhase = challenge.phase === OTP_PHASE.SEND_FAILED
      ? challenge.resumePhase || OTP_PHASE.VERIFICATION
      : challenge.phase;
    if (resumePhase === OTP_PHASE.ENROLLMENT && !challenge.enrollmentReady) {
      updateChallenge({ phase: OTP_PHASE.ENROLLMENT, resumePhase: null });
      dispatch({ type: 'CLEAR_OTP_SEND_FAILED' });
      dispatch({ type: 'OTP_PHASE', payload: { phase: OTP_PHASE.ENROLLMENT } });
      return { redirectTo: getOtpRoute(OTP_PHASE.ENROLLMENT) };
    }
    try {
      const response = await sendAdminOtp({ variables: { challengeToken: challenge.token } });
      updateChallenge({ phase: resumePhase, resumePhase: null });
      dispatch({ type: 'CLEAR_OTP_SEND_FAILED' });
      dispatch({ type: 'OTP_PHASE', payload: { phase: resumePhase } });
      return { ...response.data.sendAdminOtp, redirectTo: getOtpRoute(resumePhase) };
    } catch (err) {
      markSendFailure(err, resumePhase);
      throw err;
    }
  };

  const verifyOtp = async (code) => {
    const response = await verifyAdminOtp({
      variables: { challengeToken: challengeToken(), code }
    });
    return completeAuthentication(response.data.verifyAdminOtp);
  };

  const verifyBackupCode = async (backupCode) => {
    const response = await verifyAdminBackupCode({
      variables: { challengeToken: challengeToken(), backupCode }
    });
    return completeAuthentication(response.data.verifyAdminBackupCode);
  };

  const useBypassToken = async (bypassToken) => {
    const response = await useAdminOtpBypass({
      variables: { challengeToken: challengeToken(), bypassToken }
    });
    return completeAuthentication(response.data.useAdminOtpBypass);
  };

  const logout = () => {
    setSession(null);
    clearPendingStorage();
    clearAbilityStorage();
    dispatch({ type: 'LOGOUT' });
    cacheResetRef.current = apolloClient.clearStore().catch(() => undefined);
    return cacheResetRef.current;
  };

  useEffect(() => {
    if (isValidToken(accessToken)) {
      const tokenAccountId = jwtDecode(accessToken).id;
      let responseAccountId = null;
      if (data && data.me) {
        const { _id: currentAccountId } = data.me;
        responseAccountId = currentAccountId;
      }
      const responseMatchesToken = data
        && data.me
        && String(responseAccountId) === String(tokenAccountId);
      if (!loading && responseMatchesToken) {
        dispatch({ type: 'INITIALISE', payload: { isAuthenticated: true, user: data.me } });
      }
      if (!loading && error) {
        setSession(null);
        dispatch({ type: 'INITIALISE', payload: { isAuthenticated: false, user: null } });
      }
      return;
    }
    const challenge = getStoredChallenge();
    if (challenge) {
      dispatch({
        type: 'OTP_PENDING',
        payload: {
          otpEnabled: challenge.otpEnabled,
          otpPhase: challenge.phase,
          maskedMobile: challenge.maskedMobile,
          enrollmentCodes: getStoredEnrollmentCodes(challenge)
        }
      });
    } else {
      dispatch({ type: 'INITIALISE', payload: { isAuthenticated: false, user: null } });
    }
  }, [accessToken, loading, error, data]);

  if (!state.isInitialised) return <SplashScreen />;
  return (
    <AuthContext.Provider value={{
      ...state,
      method: 'JWT',
      login,
      logout,
      sendOtp,
      setupOtp,
      verifyOtp,
      verifyBackupCode,
      useBypassToken,
      setOtpPhase,
      clearOtpSendFailed: () => dispatch({ type: 'CLEAR_OTP_SEND_FAILED' })
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node
};

export default AuthContext;
